import * as Sentry from '@sentry/vue'
import { Crisp } from 'crisp-sdk-web'
import { getOpenReplayTracker } from './openReplay'

/**
 * Additional data that can be attached to events
 */
export interface EventData {
  [key: string]: any
}

/**
 * Check if we're in development environment
 * This is extracted to a separate function for easier testing
 */
export function isDevEnvironment(): boolean {
  return import.meta.env.DEV
}

/**
 * Records a user behavior event across all observability platforms:
 * - Crisp (for customer support context)
 * - OpenReplay (for session replay analysis)
 * - PostHog (for product analytics)
 *
 * @param eventName - The name of the event to record
 * @param extraData - Optional additional data to attach to the event
 * @param options - Optional configuration options (for testing)
 * @param options.forceInDev - Whether to force sending events in development environment
 */
export function recordEvent(
  eventName: string,
  extraData?: EventData,
  options?: { forceInDev?: boolean }
): void {
  // Skip sending events in development environment unless forced (for testing)
  if (isDevEnvironment() && !options?.forceInDev) {
    return
  }

  const eventData: EventData = {
    timestamp: new Date().toISOString(),
    source: 'user_action',
    ...extraData,
  }

  // Record event in Crisp for customer support context
  try {
    Crisp.session.pushEvent(eventName, eventData)
  } catch (error) {
    console.warn('Failed to send event to Crisp:', error)
    Sentry.captureException(error, {
      tags: { service: 'crisp', eventName },
      contexts: { eventData },
    })
  }

  // Record event in OpenReplay for session replay analysis
  try {
    const openReplayTracker = getOpenReplayTracker()
    if (openReplayTracker) {
      openReplayTracker.event(eventName, eventData)
    }
  } catch (error) {
    console.warn('Failed to send event to OpenReplay:', error)
    Sentry.captureException(error, {
      tags: { service: 'openreplay', eventName },
      contexts: { eventData },
    })
  }

  // Record event in PostHog for product analytics
  try {
    // @ts-expect-error PostHog is loaded globally
    if (window.posthog) {
      // @ts-expect-error PostHog is loaded globally
      window.posthog.capture(eventName, eventData)
    } else {
      console.warn('PostHog not available for event tracking')
    }
  } catch (error) {
    console.warn('Failed to send event to PostHog:', error)
    Sentry.captureException(error, {
      tags: { service: 'posthog', eventName },
      contexts: { eventData },
    })
  }
}
